import React, { MutableRefObject, ReactElement, useContext, useEffect, useMemo, useRef, useState } from 'react'
import {
  Box,
  Card,
  CardContent,
  Divider,
  Grid,
  Stack,
  Typography
} from '@mui/material'
import {
  DataGrid,
  GridToolbarQuickFilter,
  gridVisibleSortedRowIdsSelector
} from '@mui/x-data-grid'
import { invoiceListStyles } from './invoiceListStyles'
import { RouteBuilderContext, RouteBuilderTab } from '../../RouteBuilder'
import {
  InvoicesResponse
} from '../../../../utilities/services/DeliveryRouteService'
import { SnackbarContext } from '../../../../utilities/contexts/SnackbarContext'
import Button from '@mui/material/Button'
import language from '../../../../language/language'
import { LanguageContext } from '../../../../utilities/contexts/LanguageContext'
import Colors from '../../../../assets/Colors'
import CachedIcon from '@mui/icons-material/Cached'
import { RemoveCircleOutline, CheckOutlined, AddOutlined } from '@mui/icons-material'
import { StoreContext } from '../../../../utilities/contexts/StoreContext'
import { ReactComponent as RemoveIcon } from '../../../../assets/removeInvoice.svg'
import { ReactComponent as HoldIcon } from '../../../../assets/holdInvoice.svg'
import { ReactComponent as ReleaseIcon } from '../../../../assets/releaseInvoice.svg'
import DateFilter from '../DateFilter/DateFilter'
import { makeEndDate } from "../DateFilter/Dates";
import { useFlags } from 'launchdarkly-react-client-sdk'
import { isApac } from '../../../../utilities/helpers/RegionHelper'
import { isEmptyStr } from '../../../../utilities/helpers/AuthHelper'
import { renderSkeleton, searchAllPartDescriptions, searchAllPartNumbers } from './InvoiceStaticComponents/InvoiceStaticComponents'
import { StopAutoDispatchModal } from '../StopAutoDispatchModal/StopAutoDispatchModal'
import { useHandleInvoiceButtons } from './InvoiceApiComponent/InvoiceApiComponent'
import { getInvoiceListColumns } from './InvoiceListColumns/InvoiceListColumns'
import LoadingOverlay from '../../../../components/LoadingOverlay/LoadingOverlay'

enum ActionType {
  remove = 'remove',
  markAsDelivered = 'markAsDelivered'
}

type BulkInvoices = {
  invoiceNumber: string
  invoiceDateTime: number
}

type RowProps = {
  id: number,
  invoice: string,
  customer: string,
  orderChannel: string,
  orderNumber: string,
  orderedAt: number,
  priority: number | undefined | null,
  timeElapsed: number,
  deliveryNote: string,
  parts: string
  partsDescription: string
  serviceLevel: string
  invoiceDetails: InvoicesResponse,
  autoDispatch: Boolean,
  autoDispatchTime: number,
  hold: boolean,
  invoiceDateTime: number
  location: any,
  multipleAddress: boolean | undefined,
  reqAddressUpdate: boolean | undefined
}

type InvoiceListProps = {
  invoices: InvoicesResponse[] | undefined
  isLoadingInvoices: boolean
  setStartAndEndDates: any
  startDate: number
  endDate: number
  isErrorInvoices: boolean
  fetchInvoices: () => void
  setInvoiceDetails: (value: any) => void
  setOpenEditInvoice: (value: boolean) => void
  setOpenInvoiceActions: (value: boolean) => void,
  setInvoiceActionType: (value: ActionType) => void
  setStartDateFilter: (value: number) => void
  setEndDateFilter: (value: number) => void
  currentTab: RouteBuilderTab | null,
  setOpenLocationUpdate: (value: boolean) => void
  setLocationInvoiceDetails: (value: any) => void
  setIsBuildRoute: (value: boolean) => void
  setManualAddInvoiceOpen: (value: boolean) => void
  bulkInvoices: BulkInvoices[] | []
  setBulkInvoices: (value: BulkInvoices[]) => void
  clearSelectedInvoiceData: () => void
  isGridUpdating: boolean
  setIsGridUpdating: (value: boolean) => void
}

const InvoiceList = ({
  invoices,
  isLoadingInvoices,
  setStartAndEndDates,
  startDate,
  endDate,
  isErrorInvoices,
  setStartDateFilter,
  setEndDateFilter,
  setInvoiceDetails,
  setLocationInvoiceDetails,
  setOpenEditInvoice,
  currentTab,
  fetchInvoices,
  setOpenInvoiceActions,
  setInvoiceActionType,
  setOpenLocationUpdate,
  setIsBuildRoute,
  setManualAddInvoiceOpen,
  bulkInvoices,
  setBulkInvoices,
  clearSelectedInvoiceData,
  isGridUpdating,
  setIsGridUpdating
}: InvoiceListProps) => {
  const { selectedInvoices, setSelectedInvoices, setFilteredInvoices } =
    useContext(RouteBuilderContext)
  const { addSnack } = useContext(SnackbarContext)
  const { isCustomStopEnabledApac } = useFlags()
  const { currentLanguage } = useContext(LanguageContext)
  const [displayInvoices, setDisplayInvoices] = useState<InvoicesResponse[] | null>()
  const timer: MutableRefObject<any> = useRef(null);
  const [needsRefresh, setNeedsRefresh] = useState<boolean>(false)
  const windowSize = useRef([window.innerWidth, window.innerHeight])
  const interacted = useRef<boolean>(false)
  const oneMin = 60000
  const fiveMin = 5 * oneMin

  const [stopAutoDash, setStopAutoDash] = useState<boolean>(true)
  const [nowAutoDash, setNowAutoDash] = useState<boolean>(true)
  const [isDisabled, setIsDisabled] = useState<boolean>(true)
  const [selectedInvoiceData, setSelectedInvoiceData] = useState<InvoicesResponse | null>(null)
  const { autoDispatchEnabled, isCompanyOwned } = useContext(StoreContext)
  const [openDialog, setOpenDialog] = useState(false)
  const [selectedReason, setSelectedReason] = useState('')
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(100)
  const [sortModel, setSortModel] = useState<any>({ field: 'orderedAt', sort: 'desc' })
  const [holdLoading, setHoldLoading] = useState(false)
  const { handleOnHold, startAutoDispatch, stopAutoDispatch } = useHandleInvoiceButtons()

  const handleOpenDialog = (): void => {
    setOpenDialog(true)
  }

  const handleCloseDialog = (): void => {
    setOpenDialog(false)
    setSelectedReason('')
  }

  const handleConfirmStopAutoDispatch = async (): Promise<void> => {
    handleCloseDialog()
    const request = {
      selectedReason, setIsGridUpdating, bulkInvoices, setIsDisabled, clearSelectedInvoiceData, fetchInvoices
    }
    await stopAutoDispatch(request)
  }

  useEffect(() => {
    if (invoices) {
      //TODO: This is fixing the invoice list not being updated immediately // test
      setDisplayInvoices(invoices)
      if (interacted.current) {
        //TODO: Do we still need this popup?
        // addSnack({
        //   severity: 'info',
        //   message: 'New invoice data is available.',
        //   action: {
        //     prompt: 'refresh',
        //     callback: () => {
        interacted.current = false
        setDisplayInvoices(invoices)
        //   }
        // }
        // })
      } else {
        setDisplayInvoices(invoices)
      }
    } else {
      setDisplayInvoices(null)
    }
  }, [invoices, addSnack, displayInvoices])

  const onSearch = () => {
    const start = new Date(startDate)
    start.setHours(0, 0, 0, 0)
    const end = new Date(endDate)
    startDate && endDate && setStartAndEndDates(start.getTime(), makeEndDate(end.getTime()))
    setDisplayInvoices(null)
    setPage(0)
    setPageSize(100)
    setSortModel({ field: 'orderedAt', sort: 'desc' })
  }

  const resetAll = () => {
    const start = new Date(new Date().getTime() - 72 * 60 * 60 * 1000)
    start.setHours(0, 0, 0, 0)
    const end = new Date()
    setStartAndEndDates(start.getTime(), makeEndDate(end.getTime()))
  }

  const InvoiceDataGrid: ReactElement = useMemo(() => {
    //timer to refresh time elapsed column
    !!timer && !!timer.current && clearTimeout(timer.current)
    timer.current = setTimeout(() => {
      setNeedsRefresh(!needsRefresh)
    }, 200000);

    const InvoiceComponent = () =>  <Box sx={invoiceListStyles.filterContainer}>
      <Grid item container xs={12} sx={invoiceListStyles.dispatchButtonContainer} paddingBottom={'0px'}>
        <Grid item xs={5} display={'flex'} justifyContent={'start'}>
          {(!isApac || (isCustomStopEnabledApac && isApac)) && <Button
            data-testid='insert-invoice'
            onClick={() => {
              setManualAddInvoiceOpen(true)
            } }
            sx={invoiceListStyles.insertInvoice}
          >
            <AddOutlined fontSize="medium" />
            {language[currentLanguage].insertInvoice}
          </Button>}
        </Grid>
        <Grid item xs={7} display={'flex'} justifyContent={'end'}>
          {autoDispatchEnabled &&
            <>
              <Button startIcon={<CachedIcon />} sx={invoiceListStyles.startAutoDispatchButton}
                disabled={nowAutoDash} onClick={async () => {
                  await startAutoDispatch({ selectedInvoiceData, clearSelectedInvoiceData, fetchInvoices })
                } }>{language[currentLanguage].autoDispatchNow}
              </Button>
              <Button disabled={stopAutoDash} startIcon={<RemoveCircleOutline />}
                sx={invoiceListStyles.stopAutoDispatch}
                onClick={handleOpenDialog}>{language[currentLanguage].stopDispatchNow}
              </Button>
            </>}
          <Button
            disabled={isDisabled}
            data-testid='hold-invoice'
            onClick={async () => {
              setHoldLoading(true)
              await handleOnHold({ setIsGridUpdating, bulkInvoices, selectedInvoiceData, setOpenInvoiceActions, setInvoiceDetails, setIsDisabled, clearSelectedInvoiceData })
              setHoldLoading(false)
              fetchInvoices()
            } }
            sx={invoiceListStyles.startAutoDispatchButton}>
            {currentTab === 'holdInvoices' ? <ReleaseIcon style={{ fill: isDisabled ? Colors.napaGreyDisabled2 : Colors.napaYellow3 }} /> :
              <HoldIcon style={{ fill: isDisabled ? Colors.napaGreyDisabled2 : Colors.black }} />}
            {currentTab === 'holdInvoices'
              ? language[currentLanguage].releaseHold
              : language[currentLanguage].placeHold}
          </Button>
          <Button
            disabled={isDisabled}
            data-testid='mark-as-delivered'
            onClick={() => {
              setOpenInvoiceActions(true)
              setInvoiceActionType(ActionType.markAsDelivered)
            } }
            sx={invoiceListStyles.startAutoDispatchButton}>
            <CheckOutlined fontSize="medium" />
            {language[currentLanguage].markDelivered}
          </Button>
          <Button
            disabled={isDisabled}
            data-testid='remove-invoice'
            onClick={() => {
              setOpenInvoiceActions(true)
              setInvoiceActionType(ActionType.remove)
            } } sx={invoiceListStyles.removeButton}>
            <RemoveIcon style={{ fill: isDisabled ? Colors.napaGreyDisabled2 : Colors.napaRed3 }} />
            {language[currentLanguage].remove}
          </Button>
        </Grid>
      </Grid>
    </Box>
    const QuickSearchToolbar = () => {
      const [searchFilter, setSearchFilter] = useState<string>('')

      return (
        <Box sx={{ p: 0.5, pb: 0 }}>
          <Grid container spacing={2}>
            <Grid item style={invoiceListStyles.searchBarBox} md={5}>
              <GridToolbarQuickFilter
                sx={invoiceListStyles.searchBar}
                placeholder={language[currentLanguage].searchInvoice}
                quickFilterParser={(searchInput: string) => {
                  setSearchFilter(searchInput)
                  interacted.current = true
                  return searchInput
                    .split(' ')
                    .map((value) => value.trim())
                    .filter((value) => value !== '')
                }}
              />
            </Grid>
            <Grid item style={invoiceListStyles.searchBarBox}>
              <DateFilter
                startDate={startDate}
                setStartDateFilter={setStartDateFilter}
                endDate={endDate}
                setEndDateFilter={setEndDateFilter}
                onSearch={onSearch}
                resetAll={resetAll}
              />
            </Grid>
          </Grid>
          <InvoiceComponent />
          <Divider />
          {searchFilter ? (
            <Box style={invoiceListStyles.searchResultsBox}>
              <Typography style={invoiceListStyles.searchResultsFont}>
                {language[currentLanguage].result} "{searchFilter}"
              </Typography>
            </Box>
          ) : null}
        </Box>
      )
    }

    let columns = getInvoiceListColumns({
      language,
      currentLanguage,
      isCompanyOwned,
      setInvoiceDetails,
      setOpenEditInvoice,
      setLocationInvoiceDetails,
      setIsBuildRoute,
      setOpenLocationUpdate
    })

    if (!autoDispatchEnabled) {
      columns = columns.filter((col) => col.field !== 'autoDispatchTime')
    }

    if (!isCompanyOwned || isApac) {
      columns = columns.filter((col) => col.field !== 'address')
    }

    if (!isApac) {
      columns = columns.filter((col) => !['orderNumber', 'orderChannel'].includes(col.field))
    }

    const visibleRows = () => {
      const sorted = [...setRow()]
      const { field, sort } = sortModel

      if (!field || !sort) return sorted

      sorted.sort((a, b) => {
        const aValue = a[field as keyof RowProps]
        const bValue = b[field as keyof RowProps]

        if (aValue == null || bValue == null) return 0

        if (typeof aValue === 'number' && typeof bValue === 'number') {
          return sort === 'asc' ? aValue - bValue : bValue - aValue
        }

        if (typeof aValue === 'string' && typeof bValue === 'string') {
          const result = aValue.localeCompare(bValue, undefined, { sensitivity: 'base' })
          if (result !== 0) return sort === 'asc' ? result : -result
        }
        return 0
      })
      return sorted
    }

    let setRow = () => {
      return (displayInvoices?.filter(item => !isEmptyStr(item.invoiceNumber))?.map((invoice) => {
        return {
          id: isApac ? invoice.invoiceNumber : Number(invoice.invoiceNumber),
          invoice: invoice.invoiceNumber,
          customer: invoice.deliveryAddress.name,
          orderChannel: invoice?.orderChannel,
          orderNumber: invoice?.orderNumber,
          orderedAt: invoice.invoiceDateTime,
          priority: invoice.deliveryPriorityInMins,
          timeElapsed: Date.now() - invoice.invoiceDateTime,
          //At the moment, no delivery notes information exists on the invoice data object --
          // do we want to create a field where users can add notes to an invoice?
          deliveryNote: '',
          parts: searchAllPartNumbers(invoice.parts),
          partsDescription: searchAllPartDescriptions(invoice.parts),
          serviceLevel: invoice.serviceLevel,
          invoiceDetails: invoice,
          autoDispatch: invoice.autoDispatch,
          autoDispatchTime: fiveMin - Date.now() - invoice.invoiceDateTime,
          hold: invoice?.hold,
          invoiceDateTime: invoice.invoiceDateTime,
          location: invoice?.deliveryAddress,
          multipleAddress: invoice?.multipleAddresses,
          reqAddressUpdate: invoice?.requiresAddressUpdate,
        }
      }) || [])
    }

    const renderDataGrid = () => {
      return (
        <DataGrid
          style={{ height: `${windowSize.current[1] - 250}px` }}
          components={{
            Toolbar: QuickSearchToolbar,
            NoRowsOverlay: () => (
              <Stack height="100%" alignItems="center" justifyContent="center">
                {language[currentLanguage].noResults}
              </Stack>
            ),
            NoResultsOverlay: () => (
              <Stack height="100%" alignItems="center" justifyContent="center">
                {language[currentLanguage].noResults}
              </Stack>
            )
          }}
          onStateChange={(state) => {
            const newRows = gridVisibleSortedRowIdsSelector(state)
            setFilteredInvoices(newRows)
          }}
          disableColumnSelector={true}
          onPageSizeChange={(newPage) => {
            setPageSize(newPage)
          }}
          rows={visibleRows()}
          loading={isGridUpdating}
          columns={currentTab === 'unassignedInvoices'
            ? columns.filter((column) => {
              return column.field !== 'invoiceFlagged'
            })
            : columns}
          checkboxSelection
          disableSelectionOnClick
          onSortModelChange={(model) => {
            if (model.length === 1)
              setSortModel({ field: model[0].field, sort: model[0].sort })
            else
              setSortModel({ field: '', sort: '' })
          }
          }
          initialState={{
            sorting: {
              sortModel: [sortModel]
            },
            columns: {
              columnVisibilityModel: {
                parts: false,
                partsDescription: false,
                deliveryNote: false
              }
            }
          }}
          hideFooterSelectedRowCount={true}
          disableVirtualization={process.env.NODE_ENV === 'test'}
          onPageChange={(newPage: any) => setPage(newPage)}
          onSelectionModelChange={(newSelectionModel) => {
            interacted.current = true
            const start = page * pageSize
            const end = start + pageSize

            let selectAllRows, filteredSelection
            const sortedList = visibleRows()

            const selectAll = sortedList?.slice(start, end)

            if (selectAll.every((row: any) => newSelectionModel.includes(row.id))) {
              selectAllRows = selectAll
            }
            else {
              selectAllRows = sortedList?.filter((row: any) => newSelectionModel.includes(row.id))
            }

            filteredSelection = selectAllRows?.map((row: any) => row.id)
            setSelectedInvoices(filteredSelection)
            if (newSelectionModel && newSelectionModel.length === 0) {
              setSelectedInvoices([])
              setSelectedInvoiceData(null)
              setStopAutoDash(true)
              setNowAutoDash(true)
              setIsDisabled(true)
              setBulkInvoices([])
            }
            else {
              const selectedInvoiceData = displayInvoices?.find(data => data.invoiceNumber === newSelectionModel[0].toString())
              setSelectedInvoiceData(selectedInvoiceData ?? null)
              setIsDisabled(newSelectionModel?.length === 0)
              const isAutoDispatchDisabled = selectAllRows && selectAllRows?.length > 0 && selectAllRows?.every((row: any) => row.autoDispatch === true)
              setStopAutoDash(!isAutoDispatchDisabled)
              setNowAutoDash(newSelectionModel.length === 1 ? !selectedInvoiceData?.autoDispatch : true)

              if (newSelectionModel.length > 0 && selectedInvoiceData && selectAllRows) {

                const bulkArray: BulkInvoices[] = selectAllRows?.map((invoice: any) => (
                  {
                    invoiceNumber: invoice.invoice,
                    invoiceDateTime: invoice.invoiceDateTime
                  }
                ))
                setBulkInvoices(bulkArray)
              }
            }
          }}
          selectionModel={selectedInvoices}
          sx={invoiceListStyles.gridSize}
          componentsProps={{
            toolbar: {
              showQuickFilter: true,
              quickFilterProps: { debounceMs: 500 }
            }
          }} />
      )
    }
    return renderDataGrid()
    // eslint-disable-next-line
  }, [
    displayInvoices,
    selectedInvoices,
    setSelectedInvoices,
    setFilteredInvoices,
    currentLanguage,
    startDate,
    endDate,
    needsRefresh,
    page,
    pageSize,
    sortModel,
    isGridUpdating
  ])

  const NoInvoicesCard = () => {
    return (
      <Grid md={12} item>
        <DateFilter
          startDate={startDate}
          setStartDateFilter={setStartDateFilter}
          endDate={endDate}
          setEndDateFilter={setEndDateFilter}
          onSearch={onSearch}
          resetAll={resetAll}
        />
        <Card sx={invoiceListStyles.noInvoicesCard}>
          <CardContent>
            <div style={invoiceListStyles.noInvoicesCardContent}>
              <Typography sx={invoiceListStyles.noInvoices}>
                {language[currentLanguage].noInvoices}
              </Typography>
            </div>
          </CardContent>
        </Card>
      </Grid>
    )
  }

  return (
    <Grid item container sx={invoiceListStyles.allInvoicesContainer}>
      {holdLoading && <LoadingOverlay isLoading={holdLoading} />}
      {isLoadingInvoices ? (
        renderSkeleton()
      ) : (
        <Grid item container xs={12} sx={invoiceListStyles.dataGridStyle}>
          {/* <DateFilter /> */}
          {!isErrorInvoices ? ( //displayInvoices && displayInvoices.length > 0 && !isErrorInvoices
            InvoiceDataGrid
          ) : (
            <NoInvoicesCard />
          )}
        </Grid>
      )}
      <StopAutoDispatchModal
        openDialog={openDialog}
        handleCloseDialog={handleCloseDialog}
        selectedReason={selectedReason}
        setSelectedReason={setSelectedReason}
        handleConfirmStopAutoDispatch={handleConfirmStopAutoDispatch}
      />
    </Grid>
  )
}

export default InvoiceList